import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { AuthApi } from '../api/auth.api';
import {
  ILoginRequestParameter,
  IPostTokenBody,
} from '../interfaces/login.interface';
import { objToParams } from '../utils/url';
import RootStore from './RootStore';
import {
  getLocalStorageItem,
  LocalStorageKeyEnum,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../utils/local-storage-handler';

type IHint = {
  name: string;
  serviceId: number;
  serviceAttribute: number;
  token: string;
  recipientId?: string;
  categoryId?: number;
  targetId?: string;
};

export default class LoginStore {
  private rootStore: RootStore;

  @observable serviceId: string = '';
  @observable serviceAttribute: string = '';
  @observable token: string = '';
  @observable encodedToken: string = '';

  @computed
  get isCreateTokenDisabled(): boolean {
    return !this.serviceId || !this.serviceAttribute || !this.token;
  }

  @computed
  get isLoginDisabled(): boolean {
    return !this.encodedToken;
  }

  @computed
  get hintToken(): string {
    const hint = {
      authId: '1000000000',
      userName: '楽天株式会社',
      recipients: [
        {
          serviceId: 1,
          serviceAttribute: 2,
          authId: '100000',
          userName: 'P01 株式会社',
        },
      ],
    };
    return JSON.stringify(hint);
  }

  @computed
  get hints(): IHint[] {
    return [
      {
        name: 'ホテル探し人(Consumer) to 102906[STG]offsite_Shirasaka',
        serviceId: 1003,
        serviceAttribute: 104,
        token: JSON.stringify({
          authId: '9876543210',
          userName: 'ホテル探し人(Consumer)',
          recipients: [
            {
              serviceId: 1003,
              serviceAttribute: 106,
              authId: '102906',
            },
          ],
          inquiryTarget: {
            categoryId: 1,
          },
        }),
      },
      {
        name: 'ホテル予約済み人(Consumer) to 102906[STG]offsite_Shirasaka',
        serviceId: 1003,
        serviceAttribute: 104,
        token: JSON.stringify({
          authId: '39974324',
          userName: 'ホテル予約済み人(Consumer)',
          recipients: [
            {
              serviceId: 1003,
              serviceAttribute: 106,
              authId: '102906',
            },
          ],
          inquiryTarget: {
            categoryId: 2,
            targetName: '%E4%BA%88%E7%B4%84%E7%95%AA%E5%8F%B7',
            targetId: 'RYa061fdfz',
            targetUrl:
              'https%3A%2F%2Ftravel.rakuten.co.jp%2F%3FbookingCode%3Dxyz',
          },
        }),
      },
      {
        name: 'ホテル探し人(Consumer) to 31571[STG]　楽天トラベル1　18F',
        serviceId: 1003,
        serviceAttribute: 104,
        token: JSON.stringify({
          authId: '9876543210',
          userName: 'ホテル探し人(Consumer)',
          recipients: [
            {
              serviceId: 1003,
              serviceAttribute: 106,
              authId: '31571',
            },
          ],
          inquiryTarget: {
            categoryId: 1,
          },
        }),
      },
      {
        name: 'ホテル予約済み人(Consumer) to 31571[STG]　楽天トラベル1　18F',
        serviceId: 1003,
        serviceAttribute: 104,
        token: JSON.stringify({
          authId: '39974324',
          userName: 'ホテル予約済み人(Consumer)',
          recipients: [
            {
              serviceId: 1003,
              serviceAttribute: 106,
              authId: '31571',
            },
          ],
          inquiryTarget: {
            categoryId: 2,
            targetName: '%E4%BA%88%E7%B4%84%E7%95%AA%E5%8F%B7',
            targetId: 'RYa061fdfz',
            targetUrl:
              'https%3A%2F%2Ftravel.rakuten.co.jp%2F%3FbookingCode%3Dxyz',
          },
        }),
      },
    ];
  }

  @action
  initStore = () => {
    runInAction(() => {
      this.serviceId = '';
      this.serviceAttribute = '';
      this.token = '';
    });
  };

  @action
  onClickHint = (index: number) => {
    runInAction(() => {
      const hint = this.hints[index];
      this.serviceId = String(hint.serviceId);
      this.serviceAttribute = String(hint.serviceAttribute);
      this.token = hint.token;
    });
  };

  @action
  onChangeServiceId = (serviceId: string) => {
    this.serviceId = serviceId;
  };

  @action
  onChangeServiceAttribute = (serviceAttribute: string) => {
    this.serviceAttribute = serviceAttribute;
  };

  @action
  onChangeToken = async (token: string) => {
    this.token = token;
  };

  @action
  createToken = async () => {
    const body: IPostTokenBody = JSON.parse(this.token);

    let inquiryTarget = {
      categoryId: body.inquiryTarget.categoryId,
    };

    inquiryTarget = this.appendParameter(inquiryTarget, this.token, 'targetId');
    inquiryTarget = this.appendParameter(
      inquiryTarget,
      this.token,
      'targetName',
      true,
    );
    inquiryTarget = this.appendParameter(
      inquiryTarget,
      this.token,
      'targetUrl',
    );

    const res = await AuthApi.postToken(
      Number(this.serviceId),
      Number(this.serviceAttribute),
      {
        ...body,
        ...{
          inquiryTarget: inquiryTarget,
        },
      },
    );
    runInAction(() => {
      this.encodedToken = res;
    });
  };

  @action
  simulateLoginFromExternalDomain = (removeJwtToken: boolean) => {
    if (removeJwtToken) {
      removeLocalStorageItem(LocalStorageKeyEnum.TOKEN);
    }
    removeLocalStorageItem(LocalStorageKeyEnum.LOGIN_TYPE);
    const parsed = JSON.parse(this.token);
    const recipient = parsed.recipients[0];

    let objParameter = {
      serviceId: recipient.serviceId,
      serviceAttribute: recipient.serviceAttribute,
      recipientId: recipient.authId,
      categoryId: parsed.inquiryTarget.categoryId,
    };

    objParameter = this.appendParameter(objParameter, this.token, 'targetId');
    objParameter = this.appendParameter(objParameter, this.token, 'targetName');
    objParameter = this.appendParameter(objParameter, this.token, 'targetUrl');

    this.initStore();
    window.location.href = `/login?${objToParams(objParameter)}`;
  };

  @action
  devLogin = async (destination: string) => {
    setLocalStorageItem(LocalStorageKeyEnum.LOGIN_TYPE, 'dev-login');

    // this is access_token returned from AuthApi.login (api/auth/login) // it's a JWT
    const token = await AuthApi.devLogin(
      Number(this.serviceId),
      Number(this.serviceAttribute),
      this.encodedToken,
    );
    setLocalStorageItem(LocalStorageKeyEnum.TOKEN, token);
    this.initStore();
    window.location.href = `/${destination}`;
  };

  isDevLogin = () => {
    return getLocalStorageItem(LocalStorageKeyEnum.LOGIN_TYPE) === 'dev-login';
  };

  private appendParameter = (
    srcParameter: any,
    srcToken: string,
    paramKey: keyof ILoginRequestParameter,
    decodeValueForDevLogin: boolean = false, // hmmm, this is only for local token creation at dev-login
  ) => {
    const value = JSON.parse(srcToken).inquiryTarget[paramKey];
    if (value) {
      return {
        ...srcParameter,
        [paramKey]: decodeValueForDevLogin ? decodeURIComponent(value) : value,
      };
    }
    return srcParameter;
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);
  }
}
